import { displaySectionName } from "../../../../../services/helpers/BusinessSectionsHelper";

// Keys from the backend `details` map that must never appear in the Specifications card.
// Condition/age are shown via shared attributes section.
// Rental pricing keys are shown in PricingCard.
// Everything else in this list is internal/duplicate metadata.
const BLOCKED_KEYS = new Set([
  "images",
  "age",
  "condition",
  "Condition",
  "CONDITION",
  "pid",
  "price",
  "title",
  "description",
  "category",
  "created_at",
  "CREATED_AT",
  "updated_at",
  "status",
  "sellerid",
  "sellername",
  "createdtime",
  "searchdistance",
  "verifieduser",
  "contactmode",
  "offercounts",
  "productqueries",
  "latitude",
  "longitude",
  "phone",
  "listingstatus",
  "sectionid",
  "section_id",
  // rental pricing — these belong in PricingCard
  "deposit_amount",
  "rental_per_hour",
  "rental_per_day",
  "rental_per_week",
  "rental_per_month",
  // shared attributes stored flat
  "min_rental_duration",
  "advance_notice",
  "handover_method",
  "cuisine",
  "dietary_type",
]);

export interface SpecAttribute {
  label: string;
  value: string;
  tags?: string[];  // present only when the attribute is a tags array
}

function parseTags(value: any): string[] | null {
  try {
    if (Array.isArray(value)) return value.map(String).filter(Boolean);
    const parsed = JSON.parse(value);
    if (Array.isArray(parsed)) return parsed.map(String).filter(Boolean);
  } catch {
    // Not valid JSON — likely a Python-style list repr like "[non-veg, chicken]".
    // Strip the surrounding brackets before splitting so they don't stick to
    // the first/last tag, and trim stray quotes off each piece.
    const stripped = String(value).trim().replace(/^\[/, "").replace(/\]$/, "");
    const parts = stripped
      .split(",")
      .map((s) => s.trim().replace(/^['"]|['"]$/g, ""))
      .filter(Boolean);
    if (parts.length > 1) return parts;
  }
  return null;
}

export function filterSpecAttributes(
  details: Record<string, any> | undefined
): SpecAttribute[] {
  if (!details) return [];
  return Object.entries(details)
    .filter(([key]) => !BLOCKED_KEYS.has(key) && !BLOCKED_KEYS.has(key.toLowerCase()))
    .filter(([, value]) => value !== null && value !== undefined && value !== "")
    .map(([key, value]) => {
      const label = formatSpecLabel(key);
      const lowerKey = key.toLowerCase();
      if (lowerKey === "tags") {
        const tags = parseTags(value);
        if (tags && tags.length > 0) return { label, value: "-", tags };
      }
      // Section/sub-section only: render "-" as a space for display. The
      // stored value itself is untouched — this is purely cosmetic.
      if (lowerKey === "section" || lowerKey === "subsection") {
        return { label, value: displaySectionName(String(value)) };
      }
      return { label, value: String(value) };
    });
}

function formatSpecLabel(key: string): string {
  return key
    .replace(/([a-z])([A-Z])/g, "$1 $2")  // camelCase → "camel Case"
    .replace(/[_-]/g, " ")                  // snake_case / kebab-case → spaces
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .map((word) => {
      // Preserve known acronyms and all-caps words (RAM, OS, CPU, etc.)
      if (word === word.toUpperCase() && word.length <= 5) return word;
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    })
    .join(" ");
}

// Keys that belong in PricingCard for rental listings
const RENTAL_PRICING_KEYS: Record<string, string> = {
  rental_per_hour: "Per Hour",
  rental_per_day: "Per Day",
  rental_per_week: "Per Week",
  rental_per_month: "Per Month",
};

export interface RentalTierValue {
  label: string;
  value: string;
}

export function extractRentalPricing(
  details: Record<string, any> | undefined
): RentalTierValue[] {
  if (!details) return [];
  return Object.entries(RENTAL_PRICING_KEYS)
    .filter(([key]) => details[key] != null && details[key] !== "")
    .map(([key, label]) => ({ label, value: String(details[key]) }));
}
